"use client";

import { useState } from "react";
import { Heart, Flag, Share2, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useFavorites } from "@/hooks";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface ListingActionsProps {
  listingId: string;
  listingTitle: string;
  listingSlug?: string;
  isMobile?: boolean;
}

// reason değerleri `lib/validations/report.ts` içindeki `reportReasonSchema`
// enum'u ile bire bir aynı olmalı. Önceki değerler ("fake", "scam", "incorrect")
// schema'daki ("fake_listing", "fraud", "wrong_info") ile eşleşmediği için
// API zod validation "Geçersiz veri" döndürüyordu.
const REPORT_REASONS = [
  { value: "inappropriate", label: "Uygunsuz içerik" },
  { value: "fake_listing", label: "Sahte ilan" },
  { value: "fraud", label: "Dolandırıcılık şüphesi" },
  { value: "wrong_info", label: "Yanlış bilgi" },
  { value: "spam", label: "Spam" },
  { value: "other", label: "Diğer" },
];

export function ListingActions({
  listingId,
  listingTitle,
  listingSlug,
  isMobile,
}: ListingActionsProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const [reportOpen, setReportOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportEmail, setReportEmail] = useState("");
  const [reportDescription, setReportDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const favorite = isFavorite(listingId);

  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/listings/${listingSlug || listingId}`
      : "";

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link kopyalandı!");
    } catch {
      toast.error("Link kopyalanamadı");
    }
  };

  const handleReport = async () => {
    if (!reportReason) {
      toast.error("Lütfen bir şikayet nedeni seçin");
      return;
    }

    if (!reportDescription.trim()) {
      toast.error("Lütfen şikayetinizi açıklayın");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch("/api/reports", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          listingId,
          reason: reportReason,
          description: reportDescription,
          reporterEmail: reportEmail || undefined,
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success("Şikayetiniz başarıyla gönderildi");
        setReportOpen(false);
        setReportReason("");
        setReportEmail("");
        setReportDescription("");
      } else {
        toast.error(data.message || "Bir hata oluştu");
      }
    } catch {
      toast.error("Şikayet gönderilirken bir hata oluştu");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Mobile Header Actions
  if (isMobile) {
    return (
      <div className="flex items-center gap-3">
        <button onClick={() => toggleFavorite(listingId)} className="p-1">
          <Heart className={cn("h-6 w-6", favorite && "fill-red-500 text-red-500")} />
        </button>
        <Dialog open={shareOpen} onOpenChange={setShareOpen}>
          <DialogTrigger asChild>
            <button className="p-1">
              <Share2 className="h-6 w-6" />
            </button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>İlanı Paylaş</DialogTitle>
            </DialogHeader>
            <div className="flex items-center gap-2 pt-2">
              <Input readOnly value={shareUrl} className="flex-1 text-sm" />
              <Button
                variant="outline"
                size="icon"
                onClick={handleCopyLink}
                className="shrink-0"
                aria-label="Linki kopyala"
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        <Dialog open={reportOpen} onOpenChange={setReportOpen}>
          <DialogTrigger asChild>
            <button className="p-1">
              <Flag className="h-6 w-6" />
            </button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>İlanı Bildir</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <Select value={reportReason} onValueChange={setReportReason}>
                <SelectTrigger>
                  <SelectValue placeholder="Şikayet nedeni seçin" />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_REASONS.map((reason) => (
                    <SelectItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                placeholder="E-posta adresiniz (opsiyonel)"
                value={reportEmail}
                onChange={(e) => setReportEmail(e.target.value)}
              />
              <Textarea
                placeholder="Şikayetinizi açıklayın..."
                rows={4}
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
              />
              <Button className="w-full" onClick={handleReport} disabled={isSubmitting}>
                {isSubmitting ? "Gönderiliyor..." : "Gönder"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Desktop Actions
  return (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        className="gap-2"
        onClick={() => toggleFavorite(listingId)}
      >
        <Heart className={cn("h-4 w-4", favorite && "fill-red-500 text-red-500")} />
        Favori
      </Button>
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2">
            <Share2 className="h-4 w-4" />
            Paylaş
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>İlanı Paylaş</DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-2 pt-2">
            <Input readOnly value={shareUrl} className="flex-1 text-sm" />
            <Button
              variant="outline"
              size="icon"
              onClick={handleCopyLink}
              className="shrink-0"
              aria-label="Linki kopyala"
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2">
            <Flag className="h-4 w-4" />
            Bildir
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>İlanı Bildir</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground text-sm">
            &quot;{listingTitle}&quot; ilanını bildiriyorsunuz.
          </p>
          <div className="space-y-4 pt-2">
            <Select value={reportReason} onValueChange={setReportReason}>
              <SelectTrigger>
                <SelectValue placeholder="Şikayet nedeni seçin" />
              </SelectTrigger>
              <SelectContent>
                {REPORT_REASONS.map((reason) => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="E-posta adresiniz (opsiyonel)"
              value={reportEmail}
              onChange={(e) => setReportEmail(e.target.value)}
            />
            <Textarea
              placeholder="Şikayetinizi açıklayın..."
              rows={4}
              value={reportDescription}
              onChange={(e) => setReportDescription(e.target.value)}
            />
            <Button className="w-full" onClick={handleReport} disabled={isSubmitting}>
              {isSubmitting ? "Gönderiliyor..." : "Gönder"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
